import { X, Download, ImageIcon, ChevronRight, Info, Scale, TicketPercent, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../config';

interface CompareModalProps {
    isOpen: boolean;
    onClose: () => void;
    compareList: any[];
    onRemove: (unit: any) => void;
    onClearAll?: () => void;
}

const CompareModal = ({ isOpen, onClose, compareList, onRemove, onClearAll }: CompareModalProps) => {
    // Prevent scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 lg:p-8">
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0" 
                        onClick={onClose} 
                    />
                    
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.99, y: 15 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.99, y: 15 }}
                        className="relative bg-white rounded-xl w-full max-w-[1400px] h-[90vh] shadow-2xl flex flex-col overflow-hidden border border-slate-200"
                    >
                
                {/* Header */}
                <div className="flex items-center justify-between px-8 py-4 border-b border-slate-100 bg-white shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="flex flex-col">
                            <h2 className="text-[18px] font-bold text-[#1E4164] tracking-tight">Technical Comparison</h2>
                            <p className="text-[11px] text-slate-400 font-medium">Selected properties for side-by-side analysis</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {compareList.length > 0 && onClearAll && (
                            <button 
                                onClick={onClearAll}
                                className="flex items-center gap-2 px-4 py-2 rounded-md text-red-500 hover:bg-red-50 text-[13px] font-semibold transition-all"
                            >
                                <Trash2 size={14} />
                                <span>Clear all</span>
                            </button>
                        )}
                        <button 
                            disabled={compareList.length === 0}
                            className={`flex items-center gap-2 px-6 py-2 rounded-md border text-[13px] font-semibold transition-all shadow-sm ${
                                compareList.length === 0 
                                ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed' 
                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300 active:bg-white'
                            }`}
                        >
                            <Download size={14} /> 
                            <span>Export Analysis</span>
                        </button>
                        <div className="w-px h-6 bg-slate-200 mx-1" />
                        <button 
                            onClick={onClose}
                            className="p-2 hover:bg-slate-50 rounded-lg transition-colors group text-slate-400"
                        >
                            <X size={20} className="group-hover:text-slate-600" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto custom-scrollbar bg-slate-50/10">
                    {compareList.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center p-10">
                            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 border border-slate-100 shadow-sm text-slate-200">
                                <Scale size={28} />
                            </div>
                            <h3 className="text-[18px] font-bold text-[#1E4164]">No items selected</h3>
                            <p className="text-slate-400 text-[13px] mt-2 max-w-[300px] font-medium leading-relaxed">
                                Select properties from the inventory to generate a detailed comparison view.
                            </p>
                        </div>
                    ) : (
                        <div className="p-8">
                            <div className="flex items-start gap-5 min-w-max">
                                {compareList.map((unit) => {
                                    // Extract primary image URL correctly
                                    const primaryImageCandidate = unit.images?.[0]?.image_url || unit.images?.[0] || unit.image_url || unit.main_image_url || unit.compound?.image_url;
                                    let finalImageUrl = 'https://images.unsplash.com/photo-1574362848149-11496d93a7c7?auto=format&fit=crop&q=80';
                                    
                                    if (primaryImageCandidate && typeof primaryImageCandidate === 'string') {
                                        finalImageUrl = primaryImageCandidate.startsWith('http') 
                                            ? primaryImageCandidate 
                                            : `${API_BASE_URL}${primaryImageCandidate}`;
                                    } else if (primaryImageCandidate && typeof primaryImageCandidate === 'object' && primaryImageCandidate.image_url) {
                                        const url = primaryImageCandidate.image_url;
                                        finalImageUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
                                    }

                                    const unitArea = unit.area_sqm || unit.area || 0;
                                    const pricePerMeter = unit.price && unitArea ? Math.round(unit.price / unitArea) : 0;

                                    // Payment Plans Logic
                                    const plans = unit.payment_plans || [];
                                    const displayPlans = plans.length > 0 ? plans : [{
                                        id: 'default-equal',
                                        escalation_type: 'Equal',
                                        down_payment_percentage: unit.down_payment_percentage || 5,
                                        installment_years: unit.installment_years || 8,
                                        is_default: true
                                    }];

                                    return (
                                        <div key={unit.id} className="w-[300px] flex flex-col border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:border-slate-300 transition-colors duration-300 group/card">
                                            
                                            {/* Top Card Image & Actions */}
                                            <div className="h-[180px] relative shrink-0 overflow-hidden">
                                                <img 
                                                    src={finalImageUrl} 
                                                    alt={unit.title || 'Property'} 
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { 
                                                        e.currentTarget.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80'; 
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                                                
                                                {/* Meta Info Overlays */}
                                                <div className="absolute top-3 left-3 flex flex-col gap-1.5 transition-transform duration-300">
                                                    <span className="bg-white/90 backdrop-blur-sm text-[#1E4164] text-[9px] font-bold px-1.5 py-0.5 rounded shadow-sm flex items-center gap-1 w-fit border border-slate-100 italic">
                                                        ti-{unit.id?.toString().substring(0,6)}
                                                    </span>
                                                    {unit.listing_type && (
                                                        <span className="bg-[#1E4164] text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-sm w-fit">
                                                            {unit.listing_type}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="absolute bottom-3 left-3 right-3 flex justify-between items-center text-white">
                                                    <div className="bg-black/40 backdrop-blur-md px-2 py-0.5 rounded border border-white/10 flex items-center gap-1.5">
                                                        <ImageIcon size={10} className=" opacity-80" />
                                                        <span className="text-[9px] font-bold">{Array.isArray(unit.images) ? unit.images.length : (unit.image_count || 1)}</span>
                                                    </div>
                                                </div>

                                                <button 
                                                    onClick={() => onRemove(unit)}
                                                    className="absolute top-3 right-3 w-8 h-8 bg-white/95 backdrop-blur-sm shadow-sm rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-white transition-all group-hover/card:opacity-100 opacity-0"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>

                                            {/* Core Data Section */}
                                            <div className="p-5 space-y-4">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-sky-600">
                                                        <span className="capitalize">{unit.property_type || unit.unit_type || 'Apartment'}</span>
                                                        <span className="text-slate-200">/</span>
                                                        <span className="text-slate-400 font-medium truncate">{unit.location || unit.location_name || 'Location'}</span>
                                                    </div>
                                                    <h3 className="text-[14px] font-bold text-[#111827] line-clamp-1 leading-tight tracking-tight">
                                                        {unit.compound?.name || unit.project_name || 'Project Name'}
                                                    </h3>
                                                    <p className="text-[11px] text-slate-500 font-semibold truncate italic">
                                                        {unit.developer?.name || unit.developer_name || 'Developer'}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <div className="flex items-baseline gap-1.5">
                                                        <span className="text-[16px] font-black text-[#1E4164]">{unit.price?.toLocaleString()}</span>
                                                        <span className="text-[10px] font-bold text-slate-400">EGP</span>
                                                    </div>
                                                    {pricePerMeter > 0 && (
                                                        <p className="text-[11px] text-slate-400 font-medium flex items-center gap-1">
                                                            <span className="w-1 h-1 bg-slate-200 rounded-full" />
                                                            {pricePerMeter.toLocaleString()} EGP/m²
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Payment Plans Section */}
                                            <div className="p-5 bg-slate-50/60 border-y border-slate-100 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[11px] font-bold text-slate-400">Payment plans</span>
                                                    <div className="flex items-center gap-2">
                                                        {(unit.has_offer || unit.has_offers || unit.is_offer) && (
                                                            <TicketPercent size={14} className="text-[#F0511B]" />
                                                        )}
                                                        <Info size={12} className="text-slate-300" />
                                                    </div>
                                                </div>
                                                
                                                <div className="space-y-2 max-h-[160px] overflow-y-auto custom-scrollbar pr-1">
                                                    {displayPlans.map((plan: any, idx: number) => (
                                                        <div key={idx} className="p-3 bg-white border border-slate-200 rounded-lg shadow-xs group/plan transition-colors hover:border-sky-200">
                                                            <div className="flex items-center gap-2.5">
                                                                <div className="w-4 h-4 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                                                                    <div className={`w-1.5 h-1.5 rounded-full ${idx === 0 ? 'bg-sky-500' : 'bg-slate-300'} group-hover/plan:bg-sky-500 transition-colors`} />
                                                                </div>
                                                                <div className="flex flex-col min-w-0 flex-1">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="text-[10px] font-bold text-[#1E4164] truncate">{plan.escalation_type || 'Equal'}</span>
                                                                        <span className="text-[10px] text-slate-300">•</span>
                                                                        <span className="text-[11px] font-bold text-slate-700 whitespace-nowrap">{plan.down_payment_percentage || unit.down_payment_percentage || 5}% DP, {plan.installment_years || unit.installment_years || 8} Yrs</span>
                                                                    </div>
                                                                    <span className="text-[10px] font-medium text-slate-400 truncate">
                                                                        {Math.round(unit.price * ((plan.down_payment_percentage || unit.down_payment_percentage || 5) / 100)).toLocaleString()} EGP Down
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Technical Analysis Section */}
                                            <div className="flex flex-col flex-1 divide-y divide-slate-100">
                                                <div className="px-5 py-3.5 bg-white flex items-center justify-between">
                                                    <span className="text-[12px] font-bold text-slate-700">Property details</span>
                                                    <button className="text-[11px] font-bold text-sky-600 hover:text-sky-700 flex items-center gap-1 group/btn px-2 py-1 rounded bg-sky-50/50 hover:bg-sky-50 transition-all">
                                                        <span>Layout</span>
                                                        <ChevronRight size={12} className="group-hover/btn:translate-x-0.5 transition-transform" />
                                                    </button>
                                                </div>

                                                <div className="flex flex-col bg-white">
                                                    {[
                                                        { label: 'Built area', value: unitArea > 0 ? `${unitArea} m²` : 'N/A' },
                                                        { label: 'Bedrooms', value: unit.bedrooms || '0' },
                                                        { label: 'Bathrooms', value: unit.bathrooms || '0' },
                                                        { label: 'Outdoor', value: unit.garden_area ? `${unit.garden_area} m²` : (unit.garden ? `${unit.garden} m²` : '---') },
                                                        { label: 'Floor level', value: unit.floor_number ? (unit.floor_number === '0' ? 'Ground' : `Floor ${unit.floor_number}`) : (unit.floor || 'N/A') },
                                                        { label: 'Finishing', value: unit.finishing_type || unit.finishing || 'Semi-finished' },
                                                        { label: 'Delivery', value: unit.delivery_date ? (typeof unit.delivery_date === 'string' && unit.delivery_date.includes('-') ? new Date(unit.delivery_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : unit.delivery_date) : 'N/A' }
                                                    ].map((spec, i) => (
                                                        <div key={i} className="px-5 py-3 flex items-center justify-between group/row hover:bg-slate-50 transition-colors">
                                                            <span className="text-[11px] font-semibold text-slate-400 truncate">{spec.label}</span>
                                                            <span className="text-[12px] font-bold text-slate-700 tracking-tight">{spec.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CompareModal;
